/**
 * /orgs/:orgId/users — everyone with a membership in the tenant.
 *
 * Groups stays the grant: what a person may read comes from their groups,
 * and that page says so. This one answers who belongs, and shows who is in
 * no group, since they reach no documents at all (IA.md). A row opens the
 * person in the record panel, where their role, groups, and overrides are
 * changed.
 *
 * Search and the role filter are client-side: the API returns the whole list.
 */
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { listGroupMembers, listGroups } from '@optum/api-client/endpoints/groups';
import { listOrganizationUsers } from '@optum/api-client/endpoints/organization-users';
import type { GroupMember, OrgMembership } from '@optum/api-client/types';

import { useRecordPanel } from '../../components/shell/record.js';
import { useOpenRecordPage } from '../../components/shell/record-page.js';
import { qk } from '../../lib/query-keys.js';
import { useDebounce } from '../../lib/use-debounce.js';
import { useRequiredOrgId } from '../../lib/use-current-org.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { DataTable } from '../../ui/DataTable.js';
import { Avatar, Badge, Pagination } from '../../ui/finixt.js';
import { ChipFilter, ListToolbar } from '../../ui/ListToolbar.js';
import { ListShape } from '../../ui/shapes.js';
import { StatRow } from '../../ui/StatRow.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { groupsByUser, initialsOf, ROLE_LABEL } from './org-users.js';

type RoleKey = 'all' | 'admin' | 'user';

const ROLE_FILTERS: ReadonlyArray<{ key: RoleKey; label: string }> = [
  { key: 'all', label: 'All roles' },
  { key: 'admin', label: 'Client admins' },
  { key: 'user', label: 'Client users' },
];

const PAGE_SIZE = 25;

/** The tenant's people: counts, a filterable table, and the panel behind each row. */
export function OrgUsersPage() {
  const orgId = useRequiredOrgId();
  const [role, setRole] = useState<RoleKey>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const debounced = useDebounce(search, 200);

  const users = useQuery({
    queryKey: qk.orgUsers(orgId),
    queryFn: () => listOrganizationUsers(orgId),
  });
  const groups = useQuery({ queryKey: qk.groups(orgId), queryFn: () => listGroups(orgId) });
  const groupList = groups.data ?? [];
  // One read per group, the same ones the Groups page and the tenant home make.
  const members = useQueries({
    queries: groupList.map((g) => ({
      queryKey: qk.groupMembers(orgId, g.id),
      queryFn: () => listGroupMembers(orgId, g.id),
    })),
  });
  const memberLists = members.map((m) => m.data);
  // Known only once every group's members are in; until then the column says so.
  const byUser =
    groups.data && memberLists.every((list): list is GroupMember[] => list !== undefined)
      ? groupsByUser(groupList, memberLists)
      : null;

  const all = users.data ?? [];
  const filtered = useMemo(
    () => filterRows(users.data ?? [], role, debounced),
    [users.data, role, debounced],
  );
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const inNoGroup = byUser ? all.filter((u) => !byUser.has(u.user_id)).length : null;

  return (
    <ListShape
      title="Users"
      subtitle="Everyone with a membership in this organization. What they can read comes from their groups."
    >
      <ListToolbar
        bare
        search={search}
        onSearch={(v) => {
          setSearch(v);
          setPage(0);
        }}
        placeholder="Search by email…"
        filters={
          <ChipFilter
            options={ROLE_FILTERS}
            value={role}
            onChange={(next) => {
              setRole(next);
              setPage(0);
            }}
          />
        }
      />

      {users.data ? (
        <StatRow
          tiles={[
            { label: 'Users', value: String(all.length), icon: 'group' },
            {
              label: 'Admins',
              value: String(all.filter((u) => u.role === 'platform-client-admin').length),
              icon: 'shield_person',
            },
            {
              label: 'In no group',
              value: inNoGroup === null ? '…' : String(inNoGroup),
              icon: 'person_off',
            },
            {
              label: 'Inactive',
              value: String(all.filter((u) => u.status === 'inactive').length),
              icon: 'block',
            },
          ]}
        />
      ) : null}

      {users.isPending && <LoadingBlock />}
      {users.isError && (
        <ErrorBlock
          message={users.error instanceof Error ? users.error.message : 'Failed to load'}
        />
      )}
      {users.data && all.length === 0 && (
        <EmptyBlock>No one has a membership in this organization yet.</EmptyBlock>
      )}
      {users.data && all.length > 0 && filtered.length === 0 && (
        <EmptyBlock>No users match.</EmptyBlock>
      )}
      {pageRows.length > 0 && (
        <UsersTable
          orgId={orgId}
          rows={pageRows}
          byUser={byUser}
          count={`${filtered.length} of ${all.length} users`}
        />
      )}
      {filtered.length > PAGE_SIZE && (
        <Pagination
          total={filtered.length}
          page={page + 1}
          pageSize={PAGE_SIZE}
          onChange={(next) => setPage(next - 1)}
        />
      )}
    </ListShape>
  );
}

/** The rows the search text and the role filter keep. */
function filterRows(rows: OrgMembership[], role: RoleKey, text: string): OrgMembership[] {
  const q = text.trim().toLowerCase();
  return rows.filter((u) => {
    if (role === 'admin' && u.role !== 'platform-client-admin') return false;
    if (role === 'user' && u.role !== 'platform-client-user') return false;
    return !q || u.email.toLowerCase().includes(q);
  });
}

/** The table: a row per person, opening them in the record panel. */
function UsersTable({
  orgId,
  rows,
  byUser,
  count,
}: {
  orgId: string;
  rows: OrgMembership[];
  byUser: Map<string, string[]> | null;
  count: string;
}) {
  const panel = useRecordPanel();
  const openPage = useOpenRecordPage();
  return (
    <DataTable
      count={count}
      hint="a person in no group reads no documents"
      rows={rows}
      rowKey="user_id"
      selectedKey={panel.current?.kind === 'org-user' ? panel.current.id : undefined}
      onRowClick={(u) => panel.open({ kind: 'org-user', id: u.user_id, orgId })}
      onRowOpenPage={(u) =>
        openPage(
          { kind: 'org-user', id: u.user_id, orgId },
          `/accounts/${encodeURIComponent(u.external_sub)}`,
        )
      }
      columns={[
        {
          key: 'email',
          label: 'User',
          strong: true,
          render: (u) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <Avatar initials={initialsOf(u.email)} size={32} />
              <span
                style={{
                  fontSize: 'var(--text-body-md)',
                  fontWeight: 'var(--weight-medium)',
                  color: 'var(--text-strong)',
                }}
              >
                {u.email}
              </span>
            </div>
          ),
        },
        {
          key: 'role',
          label: 'Role',
          render: (u) => (
            <Badge tone={u.role === 'platform-client-admin' ? 'info' : 'neutral'}>
              {ROLE_LABEL[u.role]}
            </Badge>
          ),
        },
        {
          key: 'groups',
          label: 'Groups',
          render: (u) => <GroupsCell names={byUser ? (byUser.get(u.user_id) ?? []) : null} />,
        },
        {
          key: 'status',
          label: 'Status',
          render: (u) => <StatusBadge status={u.status} />,
        },
      ]}
    />
  );
}

/** A person's groups by name; "In no group" stands out, since that grants nothing. */
function GroupsCell({ names }: { names: string[] | null }) {
  if (names === null) return <span style={{ color: 'var(--text-faint)' }}>…</span>;
  if (names.length === 0) {
    return (
      <span style={{ color: 'var(--warn)', fontWeight: 'var(--weight-medium)' }}>In no group</span>
    );
  }
  return <span style={{ color: 'var(--text-body)' }}>{names.join(', ')}</span>;
}
